/**
 * Cohort derivations for the instructor dashboard.
 *
 * Every value here comes out of a payload the page already fetches: the
 * tracker dashboard's rows (GET /api/tracker/teacher/dashboard) for the
 * Needs attention buckets, the documents rows (GET
 * /api/organisations/documents) for document coverage, the Students list's
 * rows (GET /api/organisations/students) for Most engaged. Nothing in this
 * module invents a number the API does not supply.
 */
import { COMPLETION_KINDS } from './completions';
import { DOCUMENT_TYPES, documentCounts, documentsHeld } from './documents';

/** Numeric coercion that treats null/undefined/'' as absent, not as zero. */
const num = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const sumBy = (students, key) => (students || []).reduce(
  (total, s) => total + (num(s?.[key]) ?? 0),
  0,
);

/**
 * "Never started": the backend has no activity for the student from any
 * source, ever (ever_active is false; the tracker's lifetime last activity,
 * not filtered by the period), and no document on file. Only an explicit
 * false counts, so a row without the field is never flagged.
 */
const hasNeverStarted = (s) => s?.ever_active === false && (num(s?.tracker_docs) ?? 0) === 0;

/**
 * The Overview's Needs attention rules, the one place they live: the
 * Overview's counts and the drill-down's rows are both this function's
 * lists. Inactive, Never started and No documents ignore the period (they
 * read the lifetime fields and the documents on file now); Accuracy keeps
 * it, and needs at least 10 questions attempted so a single wrong answer
 * cannot flag a student.
 */
const bucketMembers = (students) => {
  const list = students || [];
  return {
    never_started: list.filter(hasNeverStarted),
    inactive_30d: list.filter((s) => (num(s?.days_inactive) ?? -1) > 30),
    low_accuracy: list.filter((s) => {
      const acc = num(s?.qg_accuracy);
      const attempted = num(s?.qg_attempted) ?? 0;
      return acc != null && acc < 40 && attempted >= 10;
    }),
    no_documents: list.filter((s) => (num(s?.tracker_docs) ?? 0) === 0),
  };
};

/** The buckets in the order the Overview and the drill-down show them. */
const BUCKETS = [
  { key: 'never_started', label: 'Never started', lifetime: true },
  { key: 'inactive_30d', label: 'Inactive 30+ days', lifetime: true },
  { key: 'low_accuracy', label: 'Accuracy below 40%', lifetime: false },
  { key: 'no_documents', label: 'No documents', lifetime: true },
];

const isBucket = (key) => BUCKETS.some((b) => b.key === key);

/** The Overview's counts: by definition the length of each bucket's list. */
const triageBuckets = (students) => Object.fromEntries(
  Object.entries(bucketMembers(students)).map(([key, members]) => [key, members.length]),
);

/**
 * Document coverage across the cohort, over the rows of GET
 * /api/organisations/documents, the rows the Documents drill-down lists,
 * counted by the same rule (lib/documents documentCounts): `byType[type].n`
 * is that list's tile for the type, and the rest of the rows are its
 * "No <type>" filter. The total's denominator is the number of rows x 3,
 * since each student can hold a CV, a cover letter and a personal
 * statement.
 */
const documentCoverage = (rows) => {
  const list = rows || [];
  const total = list.length;
  const { types } = documentCounts(list);
  const docs = list.reduce((n, row) => n + documentsHeld(row), 0);
  const pct = (n, d) => (d > 0 ? Math.round((n / d) * 1000) / 10 : 0);
  return {
    byType: Object.fromEntries(DOCUMENT_TYPES.map((t) => [t.key, { n: types[t.key], pct: pct(types[t.key], total) }])),
    totalDocuments: docs,
    studentCount: total,
    capacity: total * 3,
    overallPct: pct(docs, total * 3),
  };
};

/** Median quiz accuracy over students who actually attempted a question. */
const medianAccuracy = (students) => {
  const values = (students || [])
    .filter((s) => (num(s?.qg_attempted) ?? 0) > 0)
    .map((s) => num(s?.qg_accuracy))
    .filter((v) => v != null)
    .sort((a, b) => a - b);
  if (!values.length) return null;
  const mid = Math.floor(values.length / 2);
  const median = values.length % 2 === 0
    ? (values[mid - 1] + values[mid]) / 2
    : values[mid];
  return Math.round(median * 10) / 10;
};

/**
 * Items a student completed in the period: the sum of their completions,
 * which is the leaderboard's COUNT(*) over the same rows. Null for a row
 * with no completions object, so "not loaded" never reads as zero.
 */
const itemsOf = (s) => {
  if (!s?.completions) return null;
  return COMPLETION_KINDS.reduce((total, k) => total + (num(s.completions[k.key]) ?? 0), 0);
};

/**
 * The one ranking of "Most engaged", for the Overview's card and the full
 * list: most items first, then most active days, then name, then id, as
 * the leaderboard orders them.
 */
const compareEngagement = (a, b) => (b.items - a.items)
  || ((num(b.active_days) ?? 0) - (num(a.active_days) ?? 0))
  || (a.name || '').localeCompare(b.name || '')
  || String(a.id).localeCompare(String(b.id));

/**
 * Every student of the Students list rows with at least one item, ranked
 * by compareEngagement, each with `items` and its `rank`. A student with no
 * items is left out rather than ranked, so an empty list reads as "no
 * activity".
 */
const engagedRanking = (students) => (students || [])
  .map((s) => ({ ...s, items: itemsOf(s) }))
  .filter((s) => (s.items ?? 0) > 0)
  .sort(compareEngagement)
  .map((s, i) => ({ ...s, rank: i + 1 }));

/** The Overview card's top `limit` of engagedRanking. */
const mostEngaged = (students, limit = 5) => engagedRanking(students).slice(0, limit);

export {
  mostEngaged,
  engagedRanking,
  compareEngagement,
  itemsOf,
  bucketMembers,
  BUCKETS,
  isBucket,
  triageBuckets,
  documentCoverage,
  medianAccuracy,
  hasNeverStarted,
  sumBy,
};
